/**
 * A PDF built out of captured screen frames.
 *
 * The rest of the app takes PDFs: the reader, the OCR pass, the extractor, the
 * viewer, the runsheet. A page an agent looked at on a county site and never
 * downloaded should arrive at all of that as the same kind of thing as a
 * file they dragged in, so the frames are wrapped into a real PDF here rather
 * than becoming a second class of attachment with its own handling everywhere.
 *
 * Written by hand instead of pulled from a library because the PDF needed is
 * the simplest one there is — a page per JPEG, nothing else — and a library
 * for that is a megabyte in the bundle to avoid eighty lines here.
 *
 * JPEG bytes are embedded as-is with /DCTDecode, which is what makes this
 * small: the image is already compressed and PDF speaks that compression
 * natively, so nothing is re-encoded.
 */

/**
 * Pages are sized at half the pixels they carry.
 *
 * The OCR pass renders every page at scale 2, so a page measured at half the
 * capture's pixel size rasterises back to exactly the pixels that were
 * captured — no upsampling, no forty-megapixel canvas, and the text lands at
 * the size the reader was tuned for.
 */
const POINTS_PER_PIXEL = 0.5;

const encoder = new TextEncoder();

/** Assembles a byte stream while keeping track of where each object starts. */
function ByteWriter() {
  const chunks = [];
  let length = 0;
  return {
    get length() { return length; },
    text(string) {
      const bytes = encoder.encode(string);
      chunks.push(bytes);
      length += bytes.length;
    },
    bytes(array) {
      chunks.push(array);
      length += array.length;
    },
    done() {
      const out = new Uint8Array(length);
      let at = 0;
      for (const chunk of chunks) { out.set(chunk, at); at += chunk.length; }
      return out;
    },
  };
}

/**
 * The same file, for images that are not JPEGs.
 *
 * `pages` is [{ data, filter, width, height, pointWidth, pointHeight }].
 * `filter` is the PDF filter the bytes are already in — /DCTDecode for a
 * JPEG, /FlateDecode for deflated raw RGB. Both go in untouched, which is
 * what keeps this small: PDF speaks those compressions natively, so
 * nothing is re-encoded on the way.
 *
 * The page size is given rather than derived, because what a point is
 * worth depends on the picture. A captured county page wants its pixels
 * at half size, so the OCR pass reads them back unscaled; a chain of
 * title wants a point per world unit, so a party box drawn 160 across
 * comes out 160 points across.
 */
export function pdfFromImagePages(pages) {
  if (!pages?.length) throw new Error('A PDF needs at least one page.');

  const out = ByteWriter();
  // Object 1 is the catalog and object 2 the page tree; each page then takes
  // three: the page, its image, and its content stream.
  const objectCount = 2 + pages.length * 3;
  const offsets = new Array(objectCount + 1).fill(0);

  const begin = (number) => {
    offsets[number] = out.length;
    out.text(`${number} 0 obj\n`);
  };
  const end = () => out.text('endobj\n');

  // A binary comment on line 2 is what tells anything reading this file in
  // text mode that it is not text.
  out.text('%PDF-1.4\n');
  out.bytes(new Uint8Array([0x25, 0xe2, 0xe3, 0xcf, 0xd3, 0x0a]));

  const pageNumber = (i) => 3 + i * 3;

  begin(1);
  out.text('<< /Type /Catalog /Pages 2 0 R >>\n');
  end();

  begin(2);
  const kids = pages.map((_, i) => `${pageNumber(i)} 0 R`).join(' ');
  out.text(`<< /Type /Pages /Kids [ ${kids} ] /Count ${pages.length} >>\n`);
  end();

  pages.forEach((page, i) => {
    const pageObj = pageNumber(i);
    const imageObj = pageObj + 1;
    const contentObj = pageObj + 2;
    const width = Math.max(1, Math.round(page.pointWidth));
    const height = Math.max(1, Math.round(page.pointHeight));

    begin(pageObj);
    out.text(
      `<< /Type /Page /Parent 2 0 R /MediaBox [ 0 0 ${width} ${height} ] `
      + `/Resources << /XObject << /Im0 ${imageObj} 0 R >> >> `
      + `/Contents ${contentObj} 0 R >>\n`,
    );
    end();

    begin(imageObj);
    out.text(
      '<< /Type /XObject /Subtype /Image '
      + `/Width ${page.width} /Height ${page.height} `
      + `/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /${page.filter} `
      + `/Length ${page.data.length} >>\nstream\n`,
    );
    out.bytes(page.data);
    out.text('\nendstream\n');
    end();

    // The image is drawn filling the page. `cm` scales the unit square the
    // image is defined on up to the page box.
    const content = `q\n${width} 0 0 ${height} 0 0 cm\n/Im0 Do\nQ\n`;
    begin(contentObj);
    out.text(`<< /Length ${encoder.encode(content).length} >>\nstream\n${content}endstream\n`);
    end();
  });

  const xrefAt = out.length;
  out.text(`xref\n0 ${objectCount + 1}\n`);
  out.text('0000000000 65535 f \n');
  for (let n = 1; n <= objectCount; n += 1) {
    out.text(`${String(offsets[n]).padStart(10, '0')} 00000 n \n`);
  }
  out.text(
    `trailer\n<< /Size ${objectCount + 1} /Root 1 0 R >>\nstartxref\n${xrefAt}\n%%EOF\n`,
  );

  return new Blob([out.done()], { type: 'application/pdf' });
}

